/**
 * Utilidades para Progressive Web App (PWA)
 */

/**
 * Registrar el Service Worker
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });

      console.log('[PWA] Service Worker registrado:', registration.scope);

      // Escuchar actualizaciones
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // Hay una nueva versión disponible
              console.log('[PWA] Nueva versión disponible');
              // Puedes mostrar una notificación al usuario aquí
            }
          });
        }
      });

      return registration;
    } catch (error) {
      console.error('[PWA] Error al registrar Service Worker:', error);
      return null;
    }
  }
  return null;
}

/**
 * Verificar si la app está instalada como PWA
 */
export function isPWAInstalled(): boolean {
  // En iOS
  if ((window.navigator as any).standalone) {
    return true;
  }
  
  // En Android/Chrome
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  
  return false;
}

/**
 * Mostrar prompt de instalación
 */
export function showInstallPrompt(): Promise<boolean> {
  return new Promise((resolve) => {
    // Verificar si ya está instalada
    if (isPWAInstalled()) {
      resolve(false);
      return;
    }

    // Buscar el evento beforeinstallprompt
    let deferredPrompt: any = null;

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      deferredPrompt = e;
      
      // Mostrar el prompt
      if (deferredPrompt) {
        deferredPrompt.prompt();
        deferredPrompt.userChoice.then((choiceResult: any) => {
          resolve(choiceResult.outcome === 'accepted');
          deferredPrompt = null;
        });
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Timeout después de 5 segundos
    setTimeout(() => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      if (!deferredPrompt) {
        resolve(false);
      }
    }, 5000);
  });
}

/**
 * Verificar actualizaciones del Service Worker
 */
export async function checkForUpdates(): Promise<boolean> {
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      await registration.update();
      return true;
    }
  }
  return false;
}

/**
 * Obtener estado de la conexión
 */
export function getConnectionStatus(): 'online' | 'offline' {
  return navigator.onLine ? 'online' : 'offline';
}

/**
 * Escuchar cambios en el estado de conexión
 */
export function onConnectionChange(callback: (status: 'online' | 'offline') => void): () => void {
  const handleOnline = () => callback('online');
  const handleOffline = () => callback('offline');

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Retornar función para limpiar listeners
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
}

